import { classes as classesSeed } from '../../data/classes/classesData';
import { emploiDuTemps as emploiDuTempsSeed } from '../../data/emploiDuTemps/emploiDuTempsData';
import { users as usersSeed } from '../../data/utilisateurs/usersData';
import { messages as messagesSeed } from '../../data/messages/messagesData';
import { messagesProf as messagesProfSeed } from '../../data/professeurs/messagesProfData';
import { notes as notesSeed } from '../../data/notes/notesData';
import { devoirs as devoirsSeed } from '../../data/devoirs/devoirsData';
import { paiementsData as paiementsSeed } from '../../data/paiements/paiementsData';
import { formules as formulesSeed } from '../../data/formules/formules';
import { logsData as logsSeed } from '../../data/logs/logsData';
import { etablissementData as etablissementsSeed } from '../../data/etablissements/etablissementData';

const DB_KEY = 'ss_offline_db_v1';

function safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function normalizeClassRows() {
  const rows = Array.isArray(classesSeed) ? classesSeed : [];
  return rows.map((c, idx) => ({
    id: c.id ?? idx + 1,
    nom: c.nom ?? `Classe ${idx + 1}`,
    nbEleves: c.nbEleves ?? 0,
    niveau: c.niveau ?? null,
  }));
}

function normalizeUserRows() {
  const base = Array.isArray(usersSeed) ? usersSeed : [];

  // Ensure at least one superadmin exists for navigation
  const superAdmin = {
    id: 999,
    username: 'superadmin',
    email: 'superadmin@schoolsense.local',
    role: 'superadmin',
    first_name: 'Super',
    last_name: 'Admin',
  };

  const mapped = base.map((u, idx) => {
    const email = u.email || `user${idx + 1}@schoolsense.local`;
    const username = (email.split('@')[0] || `user${idx + 1}`).toLowerCase();
    return {
      id: u.id ?? idx + 1,
      username,
      email,
      role: u.role || 'eleve',
      first_name: u.prenom || u.first_name || 'Prénom',
      last_name: u.nom || u.last_name || 'Nom',
    };
  });

  const hasSuperAdmin = mapped.some((u) => u.role === 'superadmin');
  return hasSuperAdmin ? mapped : [superAdmin, ...mapped];
}

function parseHeure(heure) {
  if (!heure) return { heure_debut: '08:00', heure_fin: '09:00' };
  const parts = String(heure).split('-').map((s) => s.trim());
  if (parts.length >= 2) {
    return { heure_debut: parts[0].slice(0, 5), heure_fin: parts[1].slice(0, 5) };
  }
  return { heure_debut: String(heure).slice(0, 5), heure_fin: String(heure).slice(0, 5) };
}

function normalizeCoursRows() {
  const rows = Array.isArray(emploiDuTempsSeed) ? emploiDuTempsSeed : [];
  return rows.map((c, idx) => {
    const { heure_debut, heure_fin } = parseHeure(c.heure);
    return {
      id: c.id ?? idx + 1,
      date: c.date || null,
      jour: c.jour || null,
      heure_debut,
      heure_fin,
      matiere: c.matiere || null,
      salle: c.salle || null,
      classe: c.classe || null,
      professeur_full_name: c.prof || null,
      professeur_username: c.prof ? String(c.prof).toLowerCase().replaceAll(' ', '.').replaceAll('\t', '.').replaceAll('\n', '.') : null,
    };
  });
}

function normalizeMessagesRows() {
  const inbox = Array.isArray(messagesSeed) ? messagesSeed : [];
  const outbox = Array.isArray(messagesProfSeed) ? messagesProfSeed : [];

  const mappedInbox = inbox.map((m, idx) => ({
    id: m.id ?? idx + 1,
    box: 'inbox',
    from: m.from || '—',
    to: m.to || null,
    date: m.date || null,
    sujet: m.sujet || '(Sans sujet)',
    contenu: m.contenu || '',
  }));

  const mappedOutbox = outbox.map((m, idx) => ({
    id: m.id ?? 1000 + idx + 1,
    box: 'sent',
    from: m.from || 'Moi',
    to: m.to || '—',
    date: m.date || null,
    sujet: m.sujet || '(Sans sujet)',
    contenu: m.contenu || '',
  }));

  return [...mappedInbox, ...mappedOutbox];
}

export function seedOfflineDb() {
  return {
    meta: {
      version: 1,
      seededAt: new Date().toISOString(),
    },
    auth: {
      currentUser: null,
    },
    classes: normalizeClassRows(),
    utilisateurs: normalizeUserRows(),
    cours: normalizeCoursRows(),
    messages: normalizeMessagesRows(),
    notes: Array.isArray(notesSeed) ? notesSeed.map((n, idx) => ({ id: n.id ?? idx + 1, ...n })) : [],
    devoirs: Array.isArray(devoirsSeed) ? devoirsSeed.map((d, idx) => ({ id: d.id ?? idx + 1, ...d })) : [],
    paiements: Array.isArray(paiementsSeed) ? paiementsSeed : [],
    formules: Array.isArray(formulesSeed) ? formulesSeed : [],
    logs: Array.isArray(logsSeed) ? logsSeed.map((l, idx) => ({ id: l.id ?? idx + 1, ...l })) : [],
    etablissements: Array.isArray(etablissementsSeed) ? etablissementsSeed : [],
    schedule: {
      bookings: [
        {
          id: 1,
          slot: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          name: 'Démo Établissement',
          email: 'contact@demo.fr',
          org: 'Lycée Démo',
          note: 'Démo offline',
          status: 'new',
        },
      ],
      closures: [],
    },
    rdv: {
      requests: [],
    },
  };
}

export function loadOfflineDb() {
  const existing = localStorage.getItem(DB_KEY);
  if (existing) {
    const parsed = safeJsonParse(existing, null);
    if (parsed && typeof parsed === 'object') return parsed;
  }
  const seeded = seedOfflineDb();
  localStorage.setItem(DB_KEY, JSON.stringify(seeded));
  return seeded;
}

export function saveOfflineDb(db) {
  localStorage.setItem(DB_KEY, JSON.stringify(db));
}

export function withOfflineDb(mutator) {
  const db = loadOfflineDb();
  const next = mutator(db) || db;
  saveOfflineDb(next);
  return next;
}

export function getCollection(db, name) {
  const v = db?.[name];
  return Array.isArray(v) ? v : [];
}

export function setCollection(db, name, rows) {
  return { ...db, [name]: Array.isArray(rows) ? rows : [] };
}

export function nextId(rows) {
  const max = (Array.isArray(rows) ? rows : []).reduce((m, r) => {
    const id = Number(r?.id);
    return Number.isFinite(id) ? Math.max(m, id) : m;
  }, 0);
  return max + 1;
}
